const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://panelai.tpvai.com/v1';

class AudioBuzzAPI {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Helper method for handling API responses
  async handleResponse(response) {
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API Error: ${response.status} - ${error}`);
    }
    
    // Handle different content types
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else if (contentType && contentType.includes('image/')) {
      return await response.blob();
    } else if (contentType && contentType.includes('text/')) {
      return await response.text();
    }
    
    return await response.blob();
  }

  // Health check
  async healthCheck() {
    const response = await fetch(`${this.baseURL}/health`);
    return this.handleResponse(response);
  }

  // Get API info
  async getApiInfo() {
    const response = await fetch(`${this.baseURL}/`);
    return this.handleResponse(response);
  }

  // Analyze WAV file
  async analyzeWavFile(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add optional parameters
    if (options.baseFreq) formData.append('base_freq', options.baseFreq);
    if (options.thresholdStrong !== undefined) formData.append('threshold_strong', options.thresholdStrong);
    if (options.stdThreshold1 !== undefined) formData.append('std_threshold_1', options.stdThreshold1);
    if (options.stdThreshold2 !== undefined) formData.append('std_threshold_2', options.stdThreshold2);
    if (options.outlierCountThreshold !== undefined) formData.append('outlier_count_threshold', options.outlierCountThreshold);

    const response = await fetch(`${this.baseURL}/analyze/wav`, {
      method: 'POST',
      body: formData,
    });

    return this.handleResponse(response);
  }

  // Analyze TXT file
  async analyzeTxtFile(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add optional parameters
    if (options.baseFreq) formData.append('base_freq', options.baseFreq);
    if (options.thresholdStrong !== undefined) formData.append('threshold_strong', options.thresholdStrong);
    if (options.stdThreshold1 !== undefined) formData.append('std_threshold_1', options.stdThreshold1);
    if (options.stdThreshold2 !== undefined) formData.append('std_threshold_2', options.stdThreshold2);
    if (options.outlierCountThreshold !== undefined) formData.append('outlier_count_threshold', options.outlierCountThreshold);

    const response = await fetch(`${this.baseURL}/analyze/txt`, {
      method: 'POST',
      body: formData,
    });

    return this.handleResponse(response);
  }

  // Convert WAV to TXT
  async convertWavToTxt(file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${this.baseURL}/convert/wav-to-txt`, {
      method: 'POST',
      body: formData,
    });

    return this.handleResponse(response);
  }

  // Batch analysis
  async analyzeBatch(files, options = {}) {
    const formData = new FormData();
    
    // Add all files
    files.forEach(file => {
      formData.append('files', file);
    });
    
    // Add optional parameters
    if (options.thresholdStrong !== undefined) formData.append('threshold_strong', options.thresholdStrong);
    if (options.stdThreshold1 !== undefined) formData.append('std_threshold_1', options.stdThreshold1);
    if (options.stdThreshold2 !== undefined) formData.append('std_threshold_2', options.stdThreshold2);
    if (options.outlierCountThreshold !== undefined) formData.append('outlier_count_threshold', options.outlierCountThreshold);

    const response = await fetch(`${this.baseURL}/analyze/batch`, {
      method: 'POST',
      body: formData,
    });

    return this.handleResponse(response);
  }
  // Generate CNN image
  async generateCnnImage(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    
    if (options.baseFreq !== undefined) formData.append('base_freq', options.baseFreq);
    if (options.nHarmonics !== undefined) formData.append('n_harmonics', options.nHarmonics);
    if (options.excludeBase !== undefined) formData.append('exclude_base', options.excludeBase);
    // Default Y-axis range: 0 ~ -100 dB (matching Python implementation)
    if (options.yMin !== undefined) formData.append('y_min', options.yMin);
    else formData.append('y_min', -100);
    if (options.yMax !== undefined) formData.append('y_max', options.yMax);
    if (options.baseline !== undefined) formData.append('baseline', options.baseline);
    if (options.fillBlue !== undefined) formData.append('fill_blue', options.fillBlue);
    if (options.showMetrics !== undefined) formData.append('show_metrics', options.showMetrics);
    if (options.deltaDb !== undefined) formData.append('delta_db', options.deltaDb);

    const response = await fetch(`${this.baseURL}/generate/cnn-image`, {
      method: 'POST',
      body: formData,
    });
    return this.handleResponse(response);
  }

  // Helper method to download blob as file
  downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

// Create singleton instance
const audioBuzzAPI = new AudioBuzzAPI();

export default audioBuzzAPI;

